import { api, getErrorMessage } from '../lib/axios';

export type ComplianceStatus = 'active' | 'suspicious' | 'expired' | 'disabled';
export type RecallStatus = 'draft' | 'active' | 'contained' | 'closed';
export type RecallSeverity = 'high' | 'medium' | 'low';
export type RoleStatus = 'active' | 'suspicious' | 'disabled';

export interface ComplianceRecord {
  id: string;
  certificate: string;
  region: string;
  owner: string;
  status: ComplianceStatus;
  validUntil: string;
  lastAuditAt: string;
  riskScore: number;
}

export interface RecallCase {
  id: string;
  productName: string;
  batchId: string;
  region: string;
  status: RecallStatus;
  severity: RecallSeverity;
  impactedUnits: number;
  initiatedAt: string;
  reason: string;
}

export interface RolePermission {
  domain: string;
  read: boolean;
  create: boolean;
  update: boolean;
  approve: boolean;
  export: boolean;
}

export interface RoleProfile {
  id: string;
  name: string;
  scope: string;
  status: RoleStatus;
  assignedUsers: number;
  updatedAt: string;
  permissions: RolePermission[];
}

export interface AssignableUser {
  id: string;
  fullName: string;
  email: string;
  roleLevel: number;
  status: string;
}

export interface RoleAssignmentUser extends AssignableUser {
  assignedAt: string;
}

const toError = (error: unknown): Error => new Error(getErrorMessage(error));

class AdminWorkflowsService {
  private unwrap<T>(response: { data: { data?: T } | T }): T {
    const payload = response.data as { data?: T };
    return (payload.data ?? response.data) as T;
  }

  async getComplianceRecords(filters: {
    query?: string;
    status?: 'all' | ComplianceStatus;
    sortBy?: 'risk-high' | 'risk-low' | 'expiry-nearest' | 'title';
  }): Promise<ComplianceRecord[]> {
    try {
      const response = await api.get('/admin/workflows/compliance', {
        params: filters,
      });
      return this.unwrap<ComplianceRecord[]>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async reviewComplianceRecord(id: string, reason?: string): Promise<ComplianceRecord> {
    try {
      const response = await api.post(`/admin/workflows/compliance/${id}/review`, { reason });
      return this.unwrap<ComplianceRecord>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async logComplianceException(id: string, reason: string): Promise<ComplianceRecord> {
    try {
      const response = await api.post(`/admin/workflows/compliance/${id}/exception`, { reason });
      return this.unwrap<ComplianceRecord>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async disableComplianceRecord(id: string, reason?: string): Promise<ComplianceRecord> {
    try {
      const response = await api.post(`/admin/workflows/compliance/${id}/disable`, { reason });
      return this.unwrap<ComplianceRecord>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async getRecallCases(filters: {
    query?: string;
    status?: 'all' | RecallStatus;
    severity?: 'all' | RecallSeverity;
    sortBy?: 'latest' | 'severity' | 'impacted';
  }): Promise<RecallCase[]> {
    try {
      const response = await api.get('/admin/workflows/recalls', {
        params: filters,
      });
      return this.unwrap<RecallCase[]>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async createRecallCase(payload: {
    product: string;
    batchId: string;
    region: string;
    severity: RecallSeverity;
    impactedUnits: number;
    reason: string;
  }): Promise<RecallCase> {
    try {
      const response = await api.post('/admin/workflows/recalls', payload);
      return this.unwrap<RecallCase>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async activateRecallCase(id: string, reason?: string): Promise<RecallCase> {
    try {
      const response = await api.post(`/admin/workflows/recalls/${id}/activate`, { reason });
      return this.unwrap<RecallCase>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async containRecallCase(id: string, reason?: string): Promise<RecallCase> {
    try {
      const response = await api.post(`/admin/workflows/recalls/${id}/contain`, { reason });
      return this.unwrap<RecallCase>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async closeRecallCase(id: string, reason: string): Promise<RecallCase> {
    try {
      const response = await api.post(`/admin/workflows/recalls/${id}/close`, { reason });
      return this.unwrap<RecallCase>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async getRoles(filters: {
    query?: string;
    status?: 'all' | RoleStatus;
    sortBy?: 'updated' | 'name' | 'members';
  }): Promise<RoleProfile[]> {
    try {
      const response = await api.get('/admin/workflows/rbac/roles', {
        params: filters,
      });
      return this.unwrap<RoleProfile[]>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async updateRoleStatus(id: string, status: RoleStatus): Promise<RoleProfile> {
    try {
      const response = await api.patch(`/admin/workflows/rbac/roles/${id}/status`, { status });
      return this.unwrap<RoleProfile>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async updateRolePermissions(id: string, permissions: RolePermission[]): Promise<RoleProfile> {
    try {
      const response = await api.put(`/admin/workflows/rbac/roles/${id}/permissions`, {
        permissions,
      });
      return this.unwrap<RoleProfile>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async getAssignableUsers(search?: string): Promise<AssignableUser[]> {
    try {
      const response = await api.get('/admin/workflows/rbac/users', {
        params: {
          search,
        },
      });
      return this.unwrap<AssignableUser[]>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async getRoleUsers(roleId: string, search?: string): Promise<RoleAssignmentUser[]> {
    try {
      const response = await api.get(`/admin/workflows/rbac/roles/${roleId}/users`, {
        params: {
          search,
        },
      });
      return this.unwrap<RoleAssignmentUser[]>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async assignUsersToRole(roleId: string, userIds: string[]): Promise<RoleProfile | null> {
    try {
      const response = await api.post(`/admin/workflows/rbac/roles/${roleId}/assign-users`, {
        userIds,
      });
      return this.unwrap<RoleProfile | null>(response);
    } catch (error) {
      throw toError(error);
    }
  }

  async removeUserFromRole(roleId: string, userId: string): Promise<RoleProfile | null> {
    try {
      const response = await api.delete(`/admin/workflows/rbac/roles/${roleId}/users/${userId}`);
      return this.unwrap<RoleProfile | null>(response);
    } catch (error) {
      throw toError(error);
    }
  }
}

export const adminWorkflowsService = new AdminWorkflowsService();
